import { Field, FieldLabel, OdinCalendar, Select, SelectOption, SidebarGroup } from '@myosh/odin-components';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type DateRangeError,
  type DateRangeOption,
  fromIsoDate,
  type IsoDateRange,
  isQuickRange,
  toIsoDate,
} from '@/utils/date-ranges';
import type { FilterSelection } from '@/utils/filter-selection';

type DateSelection = Pick<FilterSelection, 'range' | 'from' | 'to'>;

interface DateRangeFilterProps {
  range: DateRangeOption;
  dates: IsoDateRange;
  error?: DateRangeError;
  onChange: (next: DateSelection) => void;
}

export const DateRangeFilter = ({ range, dates, error, onChange }: DateRangeFilterProps) => {
  const { t } = useTranslation();
  const rangeId = useId();
  const fromId = useId();
  const toId = useId();

  const handleDateChange = (field: 'from' | 'to', value?: Date | Date[] | null) => {
    const date = Array.isArray(value) ? value[0] : value;
    if (date) {
      onChange({ range: 'custom', ...dates, [field]: toIsoDate(date) });
    }
  };

  return (
    <SidebarGroup className="gap-3">
      <Field>
        <FieldLabel htmlFor={rangeId}>{t(($) => $.quickDateRange)}</FieldLabel>
        <Select
          id={rangeId}
          value={range}
          className="w-full"
          onChange={(event) => {
            if (isQuickRange(event.target.value)) {
              onChange({ range: event.target.value, from: null, to: null });
            }
          }}
        >
          <SelectOption value="last7Days">{t(($) => $.dateRangeLast7Days)}</SelectOption>
          <SelectOption value="last30Days">{t(($) => $.dateRangeLast30Days)}</SelectOption>
          <SelectOption value="last12Months">{t(($) => $.dateRangeLast12Months)}</SelectOption>
          <SelectOption value="currentMonth">{t(($) => $.dateRangeCurrentMonth)}</SelectOption>
          <SelectOption value="currentYear">{t(($) => $.dateRangeCurrentYear)}</SelectOption>
          <SelectOption value="next12Months">{t(($) => $.dateRangeNext12Months)}</SelectOption>
          <SelectOption value="custom" disabled>
            {t(($) => $.custom)}
          </SelectOption>
        </Select>
      </Field>
      <div className="flex flex-col gap-2">
        <Field>
          <FieldLabel htmlFor={fromId}>{t(($) => $.from)}</FieldLabel>
          <OdinCalendar
            id={fromId}
            value={fromIsoDate(dates.from)}
            onChange={(value) => handleDateChange('from', value)}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor={toId}>{t(($) => $.to)}</FieldLabel>
          <OdinCalendar id={toId} value={fromIsoDate(dates.to)} onChange={(value) => handleDateChange('to', value)} />
        </Field>
      </div>
      {error && (
        <p role="alert" className="text-error text-sm">
          {error === 'overFiveYears' ? t(($) => $.dateRangeMaxFiveYears) : t(($) => $.dateRangeEndBeforeStart)}
        </p>
      )}
    </SidebarGroup>
  );
};
